import { useCallback, useEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { useUpdatingRef } from '@hooks/useUpdatingRef';
import { LOAD_MORE_THRESHOLD, ROW_ESTIMATE_HEIGHT } from './constants';

interface UseVirtualScrollProps {
    dataSource: readonly any[];
    onLoadMore?: () => Promise<void>;
    getRecordKey: (record: any) => string;
    lastViewedItems?: string[];
}

export function useVirtualScroll({ dataSource, onLoadMore, getRecordKey, lastViewedItems }: UseVirtualScrollProps) {
    const [loading, setLoading] = useState(false);

    const scrollRef = useRef<HTMLDivElement>(null);
    const headerScrollRef = useRef<HTMLDivElement>(null);

    const syncHeaderScroll = useCallback(() => {
        if (scrollRef.current && headerScrollRef.current) {
            headerScrollRef.current.scrollLeft = scrollRef.current.scrollLeft;
        }
    }, []);

    const latestLoadingRef = useUpdatingRef(loading);

    const handleReachEnd = useCallback(async (): Promise<void> => {
        if (!latestLoadingRef.current && onLoadMore) {
            setLoading(true);
            await onLoadMore();
            setLoading(false);
        }
    }, [latestLoadingRef, onLoadMore]);

    const virtualizer = useVirtualizer({
        count: dataSource.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => ROW_ESTIMATE_HEIGHT,
        overscan: 5,
        getItemKey: index => getRecordKey(dataSource[index]) ?? index,
    });

    const resetScroll = useCallback(() => {
        virtualizer.scrollToOffset(0); // This is required for local data (data never disappears)
        virtualizer.scrollOffset = 0; // This is required for remote data (data disappears and then reappears)
    }, [virtualizer]);

    const virtualItems = virtualizer.getVirtualItems();
    const totalSize = virtualizer.getTotalSize();

    const lastVirtualIndex = virtualItems.length > 0 ? (virtualItems[virtualItems.length - 1]?.index ?? -1) : -1;
    useEffect(() => {
        if (onLoadMore && dataSource.length > 0 && lastVirtualIndex >= dataSource.length - LOAD_MORE_THRESHOLD) {
            void handleReachEnd();
        }
    }, [lastVirtualIndex, dataSource.length, onLoadMore, handleReachEnd]);

    const lastViewedIndex =
        lastViewedItems && lastViewedItems.length > 0
            ? dataSource.findIndex(item => getRecordKey(item) === lastViewedItems[0])
            : -1;

    const triedToScrollToLastViewed = useRef(false);

    useEffect(() => {
        if (!dataSource.length) return;

        if (!triedToScrollToLastViewed.current && lastViewedIndex !== -1 && lastViewedIndex >= 0) {
            virtualizer.scrollToIndex(lastViewedIndex, { align: 'start' });
        }

        triedToScrollToLastViewed.current = true;
    }, [dataSource.length, lastViewedIndex, virtualizer]);

    return {
        loading,
        scrollRef,
        headerScrollRef,
        syncHeaderScroll,
        resetScroll,
        virtualItems,
        totalSize,
        measureElement: virtualizer.measureElement,
    };
}
